import { Box, Grid, Skeleton, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import GeneralDataItem from 'src/components/GeneralDataItem';
import { If } from 'src/components/If';
import { PanelLogin } from 'src/components/PanelLogin';
import Subtitle from 'src/components/Subtitle';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { getDebenturesGeneralData } from 'src/services/operation/operationDebentures';
import { DebentureSeriesGeneralData } from 'src/types/operations/debentures';

import {
  buildGeneralFields,
  buildSeriesFields,
  formatTextValue,
  GeneralDataFieldConfig,
  GeneralDataFieldSkeletonProps,
  OperationDebenturesGeneralDataProps,
  SERIES_SKELETON_COUNT,
} from './utils';

function GeneralDataFieldSkeleton({ title }: GeneralDataFieldSkeletonProps) {
  return (
    <Box p={2}>
      <Typography
        variant="subtitle1"
        sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
        {title}
      </Typography>
      <Skeleton variant="text" width="70%" />
    </Box>
  );
}

type GeneralDataFieldProps = {
  title: string;
  value: string;
  isLoading?: boolean;
};

function GeneralDataField({
  title,
  value,
  isLoading = false,
}: GeneralDataFieldProps) {
  if (isLoading) {
    return <GeneralDataFieldSkeleton title={title} />;
  }

  return <GeneralDataItem title={title} subtitle={value} />;
}

type GeneralDataFieldsGridProps = {
  fields: GeneralDataFieldConfig[];
  isLoading?: boolean;
};

function GeneralDataFieldsGrid({
  fields,
  isLoading = false,
}: GeneralDataFieldsGridProps) {
  return (
    <Grid container>
      {fields.map(({ title, value, grid }) => (
        <Grid item key={title} xs={grid.xs} sm={grid.sm} md={grid.md}>
          <GeneralDataField title={title} value={value} isLoading={isLoading} />
        </Grid>
      ))}
    </Grid>
  );
}

type SeriesDataProps = {
  series: DebentureSeriesGeneralData;
};

function SeriesData({ series }: SeriesDataProps) {
  return (
    <Box>
      <Subtitle title={`Série ${formatTextValue(series.serie)}`} />
      <GeneralDataFieldsGrid fields={buildSeriesFields(series)} />
    </Box>
  );
}

export default function OperationDebenturesGeneralData({
  hashId,
}: OperationDebenturesGeneralDataProps) {
  const { auth } = useAuthContext();
  const canViewGeneralData =
    auth?.user?.subscription?.plan?.permissions
      ?.operacoes_debentures_dados_gerais;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['debenturesGeneralData', hashId],
    queryFn: () => getDebenturesGeneralData(hashId),
    enabled: !!hashId && canViewGeneralData,
    retry: false,
  });

  const series = data?.series ?? [];

  return (
    <Box p={2}>
      <Subtitle title="Dados Gerais" />
      <Box>
        <If condition={!canViewGeneralData}>
          <PanelLogin />
        </If>
        <If condition={canViewGeneralData}>
          <GeneralDataFieldsGrid
            fields={buildGeneralFields(data)}
            isLoading={isLoading}
          />

          <If condition={isLoading}>
            <Box>
              <Subtitle title="Séries" />
              <Grid container>
                {Array.from({ length: SERIES_SKELETON_COUNT }).map(
                  (_, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      key={`series-skeleton-${index}`}>
                      <GeneralDataFieldSkeleton />
                    </Grid>
                  ),
                )}
              </Grid>
            </Box>
          </If>

          <If condition={!isLoading}>
            {series.map((serie, index) => (
              <SeriesData
                key={serie.serie ?? `serie-${index}`}
                series={serie}
              />
            ))}
          </If>

          <If condition={isError && !isLoading}>
            <Box px={2} pt={2}>
              <Typography variant="caption" color="text.secondary">
                Não foi possível carregar os Dados Gerais desta debênture.
              </Typography>
            </Box>
          </If>
        </If>
      </Box>
    </Box>
  );
}
